/**
 * Unified profile frames: static `profile_frame` and animated
 * `profile_frame_animated` (both CategoryKey PROFILE). They share the user's one
 * Frame slot in the app, so these rules mirror tezda-wallet's server-side
 * validation (tezda-service-library utils/profileFrame.ts) — the server
 * re-checks everything; this just fails fast in the form.
 *
 * Media slots (stored as MediaAssets):
 *   static:   image (required) + optional webm / ios_alpha
 *   animated: webm (Android) + ios_alpha (iOS .mov) + thumbnail (.webp), all required
 *   all slots: same aspect ratio (pixel sizes may differ), max 1024×1024
 */

export const PROFILE_FRAME_TYPES = new Set(['profile_frame', 'profile_frame_animated']);
export const isProfileFrameType = (type: string) => PROFILE_FRAME_TYPES.has(type);

export const PROFILE_FRAME_LIMITS = {
  maxVideoBytes: 15 * 1024 * 1024,
  maxImageBytes: 1 * 1024 * 1024,
  maxDimension: 1024,
};

type FrameAsset = { Type: string; Url: string; Width?: number; Height?: number };

const EXPECTED_MIME: Record<string, string> = {
  webm: 'video/webm',
  ios_alpha: 'video/quicktime',
};

const mb = (bytes: number) => `${(bytes / (1024 * 1024)).toFixed(bytes < 1024 * 1024 ? 2 : 0)} MB`;

export const readImageSize = (file: File) =>
  new Promise<{ Width: number; Height: number }>((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve({ Width: img.naturalWidth, Height: img.naturalHeight });
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(`Could not read image ${file.name}`));
    };
    img.src = url;
  });

export const readVideoSize = (file: File) =>
  new Promise<{ Width: number; Height: number } | null>((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    const done = (size: { Width: number; Height: number } | null) => {
      URL.revokeObjectURL(url);
      resolve(size);
    };
    video.onloadedmetadata = () => done({ Width: video.videoWidth, Height: video.videoHeight });
    // Chrome can't decode HEVC-with-alpha .mov — size is then unknown, not an error.
    video.onerror = () => done(null);
    video.src = url;
  });

/**
 * Checks one file queued for a frame media slot: MIME type, file size and
 * max dimensions. Returns the measured Width/Height (stored on the MediaAsset
 * so the same-dimension rule can be checked on save).
 */
export async function inspectProfileFrameFile(
  productType: string,
  mediaType: string,
  file: File
): Promise<{ Width?: number; Height?: number }> {
  const isVideo = mediaType === 'webm' || mediaType === 'ios_alpha';
  const expectedMime = EXPECTED_MIME[mediaType];
  if (expectedMime && file.type && file.type !== expectedMime) {
    throw new Error(`${mediaType} must be ${expectedMime} (got ${file.type}).`);
  }
  if (productType === 'profile_frame_animated' && mediaType === 'thumbnail' && file.type !== 'image/webp') {
    throw new Error('Animated frame thumbnail must be a WebP image (image/webp).');
  }
  if (!isVideo && !file.type.startsWith('image/')) {
    throw new Error(`${mediaType} must be an image.`);
  }

  const maxBytes = isVideo ? PROFILE_FRAME_LIMITS.maxVideoBytes : PROFILE_FRAME_LIMITS.maxImageBytes;
  if (file.size > maxBytes) {
    throw new Error(`${file.name} is ${mb(file.size)}; the limit for ${mediaType} is ${mb(maxBytes)}.`);
  }

  const size = isVideo ? await readVideoSize(file) : await readImageSize(file);
  if (!size) return {};
  if (productType === 'profile_frame' && mediaType === 'image') {
    if (file.type !== 'image/png') throw new Error('Static profile frame must be a PNG image.');
    if (size.Width !== 1024 || size.Height !== 1024) {
      throw new Error(`${file.name} is ${size.Width}×${size.Height}; new static profile frames must be exactly 1024×1024.`);
    }
  }
  const max = PROFILE_FRAME_LIMITS.maxDimension;
  if (size.Width > max || size.Height > max) {
    throw new Error(`${file.name} is ${size.Width}×${size.Height}; frames are limited to ${max}×${max}.`);
  }
  return size;
}

/**
 * Live Quest rewards are real PROFILE products so Collections can apply them,
 * but they are granted, never sold. Mirrors tezda-wallet
 * resources/utils/liveQuestRewardProduct.ts: an INACTIVE product tagged with
 * Metadata.LiveQuestRewardId skips the price rule (server stores PriceCoins 0).
 * Active products always need a price, so the Shop never lists a free item.
 */
export const isLiveQuestReward = (metadata?: Record<string, any> | null) =>
  typeof metadata?.LiveQuestRewardId === 'string' && metadata.LiveQuestRewardId.trim() !== '';

export const isFreeInactiveLiveQuestReward = (input: { IsActive?: boolean; Metadata?: Record<string, any> | null }) =>
  input.IsActive === false && isLiveQuestReward(input.Metadata);

/**
 * Shared single-currency price rule for frames and animated backgrounds.
 * `label` is plural, e.g. "profile frames".
 */
export function validateCatalogPrice(
  label: string,
  input: {
    CurrencyType: 'coin' | 'token';
    PriceCoins?: number;
    PriceTokens?: number;
    IsActive?: boolean;
    Metadata?: Record<string, any> | null;
  }
) {
  if (isFreeInactiveLiveQuestReward(input)) return;
  const unpriced =
    (input.CurrencyType === 'coin' && !(Number(input.PriceCoins) > 0)) ||
    (input.CurrencyType === 'token' && !(Number(input.PriceTokens) > 0));
  if (unpriced && isLiveQuestReward(input.Metadata)) {
    throw new Error(
      'Live Quest rewards are granted, not sold, so they must stay Inactive (that keeps them out of the Shop). ' +
        'Set Active off to save without a price.'
    );
  }
  if (input.CurrencyType === 'coin' && !(Number(input.PriceCoins) > 0)) {
    throw new Error(`Coin-priced ${label} need a price in coins greater than 0.`);
  }
  if (input.CurrencyType === 'token' && !(Number(input.PriceTokens) > 0)) {
    throw new Error(`Token-priced ${label} need a price in tokens greater than 0.`);
  }
}

/** Throws with a form-facing message when a frame product isn't valid. */
export function validateProfileFrameProduct(input: {
  Type: string;
  CurrencyType: 'coin' | 'token';
  PriceCoins?: number;
  PriceTokens?: number;
  IsActive?: boolean;
  Metadata?: Record<string, any> | null;
  MediaAssets: FrameAsset[];
}) {
  if (!isProfileFrameType(input.Type)) return;

  validateCatalogPrice('profile frames', input);

  const insecure = input.MediaAssets.find((a) => !/^https:\/\//i.test(a.Url));
  if (insecure) throw new Error(`Profile frame asset URLs must use HTTPS (${insecure.Type}).`);

  const byType = new Map(input.MediaAssets.map((a) => [a.Type, a]));
  const ext = (url: string) => url.split(/[?#]/)[0].split('.').pop()?.toLowerCase();
  if (byType.get('webm') && ext(byType.get('webm')!.Url) !== 'webm') throw new Error('Android asset must be a .webm file.');
  if (byType.get('ios_alpha') && ext(byType.get('ios_alpha')!.Url) !== 'mov') throw new Error('iOS asset must be a .mov file.');
  if (input.Type === 'profile_frame_animated') {
    const thumb = byType.get('thumbnail');
    if (thumb && ext(thumb.Url) !== 'webp') throw new Error('Animated frame thumbnail must be a .webp file.');
  }

  // Same SHAPE, not same pixel size: the app scales every asset to the avatar
  // ring, so a 502×502 image and a 256×256 video line up fine. A different
  // aspect ratio (e.g. square image + 16:9 video) would not.
  const sized = input.MediaAssets.filter((a) => a.Width && a.Height);
  const ratio = (a: FrameAsset) => (a.Width as number) / (a.Height as number);
  const mismatch = sized.find((a) => Math.abs(ratio(a) - ratio(sized[0])) / ratio(sized[0]) > 0.02);
  if (mismatch) {
    throw new Error(
      `Thumbnail and videos must have the same aspect ratio (${sized[0].Type} is ${sized[0].Width}×${sized[0].Height}, ` +
        `${mismatch.Type} is ${mismatch.Width}×${mismatch.Height}).`
    );
  }
}

/** Still image first: old app versions render MediaAssets[0] as the frame image. */
export function orderProfileFrameMediaAssets<T extends { Type: string }>(type: string, assets: T[]): T[] {
  if (!isProfileFrameType(type)) return assets;
  const still = (a: T) => a.Type === 'image' || a.Type === 'thumbnail';
  return [...assets.filter(still), ...assets.filter((a) => !still(a))];
}
